/**
 * Merges structure + progress into the current term+week Subject[], derives
 * selectedSubject/selectedLesson from the URL, and owns the start/complete
 * actions. Structure and progress are fetched by StudentDataContext and passed
 * in. Start/complete apply optimistically to progress and nudge other tabs with
 * no eager refetch. BLD2-1113.
 */

import { useEffect, useMemo, useRef, useCallback } from "react";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { LESSON_STATUS, type CompletionStatus } from "@ts/enums/CompletionStatus";
import { NOTIFICATION_TYPE } from "@ts/enums/NotificationTypeEnum";
import { lessonsApi } from "@api/lessonsApi";
import type {
  Subject,
  Lesson,
  CsatTriggerPayload,
  CompleteLessonResponse
} from "@ts/interfaces/terms/api/termsApiInterface";
import type { ErrorInterface } from "@ts/interfaces/ApiResponseInterface";
import type { GradeStructureApiResponse } from "@ts/interfaces/structure/api/gradeStructureApiInterface";
import type { GradeProgressApiResponse } from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import type { NotificationInterface } from "@ts/interfaces/student/api/studentAccountApiInterface";
import { LESSON_STATUS_CHANGED_KEY } from "@hooks/useCrossTabLessonSync";
import { mergeWeekLessons } from "./mergeWeekLessons";

interface UseWeekLessonsDataParams {
  urlGradeCode: number | undefined;
  urlTermId: string | undefined;
  urlWeekId: string | undefined;
  lessonSlug: string | undefined;
  lessonId: string | undefined;
  structure: GradeStructureApiResponse | null;
  progress: GradeProgressApiResponse | null;
  structureStatus: REQUEST_STATUS;
  structureError: ErrorInterface | null;
  isReady: boolean;
  applyLessonStatusLocally: (
    usageKey: string,
    status: CompletionStatus
  ) => GradeProgressApiResponse | null;
  markProgressStale: () => void;
  dismissNotificationWhere: (
    predicate: (notification: NotificationInterface) => boolean
  ) => void;
}

interface UseWeekLessonsDataReturn {
  currentWeekSubjects: Subject[];
  weekLessonsStatus: REQUEST_STATUS;
  weekLessonsError: ErrorInterface | null;
  selectedSubject: Subject | null;
  selectedLesson: Lesson | null;
  startLesson: (params: {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<void>;
  completeLesson: (params: {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<CsatTriggerPayload | null>;
  fetchSectionContent: <T>(params: {
    section: string;
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  }) => Promise<T>;
  isReady: boolean;
}

export function useWeekLessonsData({
  urlTermId,
  urlWeekId,
  lessonSlug,
  lessonId,
  structure,
  progress,
  structureStatus,
  structureError,
  isReady,
  applyLessonStatusLocally,
  markProgressStale,
  dismissNotificationWhere
}: UseWeekLessonsDataParams): UseWeekLessonsDataReturn {
  const termIdNum = urlTermId ? Number(urlTermId) : NaN;
  const weekIdNum = urlWeekId ? Number(urlWeekId) : NaN;

  // Guard against malformed URL segments (e.g. /terms/term-abc/week-xyz).
  const hasValidWeek =
    !!urlTermId && !!urlWeekId && !Number.isNaN(termIdNum) && !Number.isNaN(weekIdNum);

  const currentWeekSubjects = useMemo(
    () =>
      hasValidWeek
        ? mergeWeekLessons(structure, progress, termIdNum, weekIdNum)
        : [],
    [hasValidWeek, structure, progress, termIdNum, weekIdNum]
  );

  // Lets the action callbacks read the latest subjects without depending on
  // (and re-creating for) every merge result.
  const subjectsRef = useRef(currentWeekSubjects);
  subjectsRef.current = currentWeekSubjects;

  // Not gated on refetch status: returning null mid-refetch unmounts the lesson
  // UI and triggers removeChild errors from Learnosity/MathJax/Plotly DOM edits.
  const selectedSubject = useMemo((): Subject | null => {
    if (!lessonSlug) return null;
    return currentWeekSubjects.find(subject => subject.slug === lessonSlug) ?? null;
  }, [lessonSlug, currentWeekSubjects]);

  const selectedLesson = useMemo((): Lesson | null => {
    if (!lessonId || !lessonSlug) return null;
    const subjectGroup = currentWeekSubjects.find(s => s.slug === lessonSlug);
    if (!subjectGroup) return null;
    return (
      subjectGroup.lessons.find(l => String(l.id) === String(lessonId)) ?? null
    );
  }, [lessonId, lessonSlug, currentWeekSubjects]);

  // Leaving a lesson lands on a progress-reading view, so reconcile with the
  // authoritative backend status. markProgressStale refetches on next
  // visibility, which fires immediately here since the tab is visible. BLD2-547.
  const prevLessonIdRef = useRef<string | undefined>(lessonId);
  useEffect(() => {
    const wasOnLesson = prevLessonIdRef.current !== undefined;
    const leftLesson = lessonId === undefined;
    prevLessonIdRef.current = lessonId;
    if (wasOnLesson && leftLesson) markProgressStale();
  }, [lessonId, markProgressStale]);

  // Shared by start and complete: guard → optimistic progress update → API call
  // → cross-tab nudge. No eager refetch in any tab. BLD2-1113.
  type LessonParams = {
    gradeId: number;
    termId: number;
    weekId: number;
    lessonId: number;
    subjectSlug: string;
  };

  const updateLessonStatus = useCallback(
    async <T>(
      params: LessonParams,
      targetStatus: LESSON_STATUS.STARTED | LESSON_STATUS.COMPLETED,
      apiCall: (p: LessonParams) => Promise<T>
    ): Promise<T | undefined> => {
      const subject = subjectsRef.current.find(
        s => s.slug === params.subjectSlug
      );
      const lesson = subject?.lessons.find(l => l.id === params.lessonId);

      if (lesson?.status === LESSON_STATUS.COMPLETED) return undefined;

      // A repeated /start on an already-started lesson must not re-apply or
      // re-broadcast. BLD2-1113.
      const statusChanged = lesson?.status !== targetStatus;

      // Optimistic update before the API call so the UI reflects the new status
      // instantly. lesson.key is the progress usage_key the merge joins on.
      if (statusChanged && lesson?.key) {
        applyLessonStatusLocally(lesson.key, targetStatus);
      }

      const response = await apiCall(params);

      if (statusChanged) {
        // Nudge other tabs; they mark progress stale and reconcile on next
        // visibility, with no eager refetch. BLD2-1113.
        localStorage?.setItem?.(
          LESSON_STATUS_CHANGED_KEY,
          Date.now().toString()
        );
      }

      return response;
    },
    [applyLessonStatusLocally]
  );

  const startLesson = useCallback(
    async (params: LessonParams) => {
      await updateLessonStatus(
        params,
        LESSON_STATUS.STARTED,
        lessonsApi.startLesson
      );
      dismissNotificationWhere(
        n =>
          n.notification_type === NOTIFICATION_TYPE.NEW_TERM_AVAILABLE &&
          n.ref_for_generated_at?.grade_code === params.gradeId &&
          n.ref_for_generated_at?.term_number === params.termId
      );
    },
    [updateLessonStatus, dismissNotificationWhere]
  );

  // Returns the csat_trigger payload only when should_fire is true, else null.
  const completeLesson = useCallback(
    async (params: LessonParams): Promise<CsatTriggerPayload | null> => {
      const response = await updateLessonStatus(
        params,
        LESSON_STATUS.COMPLETED,
        lessonsApi.completeLesson<CompleteLessonResponse>
      );
      if (!response) return null;
      const csatTrigger = response.csat_trigger ?? null;
      return csatTrigger?.should_fire === true ? csatTrigger : null;
    },
    [updateLessonStatus]
  );

  const fetchSectionContent = useCallback(
    <T,>(params: {
      section: string;
      gradeId: number;
      termId: number;
      weekId: number;
      lessonId: number;
      subjectSlug: string;
    }): Promise<T> => {
      return lessonsApi.getSectionContent<T>(params);
    },
    []
  );

  return {
    currentWeekSubjects,
    weekLessonsStatus: structureStatus,
    weekLessonsError: structureError,
    selectedSubject,
    selectedLesson,
    startLesson,
    completeLesson,
    fetchSectionContent,
    isReady: isReady && hasValidWeek
  };
}
